import React, { useState, useEffect } from 'react';
import { clientAPI } from '../../services/api';
import './ClientDownloads.css';

export default function ClientDownloads() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const response = await clientAPI.getDownloads();
      setDownloads(response.data.downloads);
    } catch (error) {
      alert('Failed to load download history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredDownloads = downloads.filter(download => {
    const matchesSearch = 
      download.project_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      download.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
      download.file_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPlatform = filterPlatform === 'all' || download.platform === filterPlatform;
    
    return matchesSearch && matchesPlatform;
  });

  if (loading) return <div className="client-downloads-loading">Loading download history</div>;

  const uniquePlatforms = [...new Set(downloads.map(d => d.platform))];
  const totalDownloads = downloads.length;
  const totalSize = downloads.reduce((sum, d) => sum + (d.file_size || 0), 0);

  return (
    <div className="client-downloads-container">
      <div className="client-downloads-header">
        <h1>Download History</h1>
        <p>Track all your downloaded project files</p>
      </div>

      {/* Stats Cards */}
      {downloads.length > 0 && (
        <div className="client-downloads-stats">
          <div className="client-stat-card">
            <div className="client-stat-label">Total Downloads</div>
            <div className="client-stat-value">{totalDownloads}</div>
          </div>
          <div className="client-stat-card">
            <div className="client-stat-label">Total Size</div>
            <div className="client-stat-value">{formatFileSize(totalSize)}</div>
          </div>
          <div className="client-stat-card">
            <div className="client-stat-label">Platforms</div>
            <div className="client-stat-value">{uniquePlatforms.length}</div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="client-downloads-filters">
        <div className="client-search-group">
          <span className="client-search-icon">🔍</span>
          <input
            type="text"
            className="client-search-input"
            placeholder="Search by project, version, or file name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="client-filter-group">
          <label className="client-filter-label">Platform</label>
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="client-filter-select"
          >
            <option value="all">All Platforms</option>
            {uniquePlatforms.map(platform => (
              <option key={platform} value={platform}>
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </option>
            ))}
          </select>
        </div>
        {(searchTerm || filterPlatform !== 'all') && (
          <button
            className="client-filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setFilterPlatform('all');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Downloads Table */}
      <div className="client-downloads-table-wrapper">
        {filteredDownloads.length === 0 ? (
          <div className="client-downloads-empty">
            <div className="client-downloads-empty-icon">📥</div>
            <h2>{downloads.length === 0 ? 'No Downloads Yet' : 'No Matches Found'}</h2>
            <p>
              {downloads.length === 0
                ? 'Start by downloading project files from the My Projects page.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
          </div>
        ) : (
          <table className="client-downloads-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Version</th>
                <th>Platform</th>
                <th>File Name</th>
                <th>Size</th>
                <th>Downloaded</th>
              </tr>
            </thead>
            <tbody>
              {filteredDownloads.map((download, index) => (
                <tr key={index}>
                  <td data-label="Project" className="client-project-cell">{download.project_title}</td>
                  <td data-label="Version">
                    <span className="client-version-cell">v{download.version}</span>
                  </td>
                  <td data-label="Platform">
                    <span className={`client-platform-badge ${download.platform.toLowerCase()}`}>
                      {download.platform.charAt(0).toUpperCase() + download.platform.slice(1)}
                    </span>
                  </td>
                  <td data-label="File Name" className="client-file-cell" title={download.file_name}>
                    {download.file_name}
                  </td>
                  <td data-label="Size" className="client-size-cell">{formatFileSize(download.file_size)}</td>
                  <td data-label="Downloaded" className="client-date-cell">{formatDate(download.downloaded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary */}
      {downloads.length > 0 && filteredDownloads.length > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'right', color: '#718096', fontSize: '14px' }}>
          Showing <strong>{filteredDownloads.length}</strong> of <strong>{downloads.length}</strong> downloads
        </div>
      )}
    </div>
  );
}
